import { useState, useEffect, useCallback } from 'react';
import { 
  getPaymentReminderTemplates, 
  getScheduledReminders, 
  schedulePaymentReminder, 
  sendPaymentReminderNow,
  cancelScheduledReminder,
  previewPaymentReminder,
  getLeads
} from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Send, Clock, DollarSign, Mail, Calendar, User, X, 
  FileText, Repeat, Eye, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export const PaymentRemindersView = () => {
  const [templates, setTemplates] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showNewReminder, setShowNewReminder] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, scheduledRes, leadsRes] = await Promise.all([
        getPaymentReminderTemplates(),
        getScheduledReminders('scheduled'),
        getLeads()
      ]);
      setTemplates(templatesRes.data);
      setScheduled(scheduledRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled reminder?')) return;
    try {
      await cancelScheduledReminder(id);
      toast.success('Reminder cancelled');
      fetchData();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background p-4" data-testid="payment-reminders-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-accent" />
            Payment Reminders
          </h2>
          <p className="text-sm text-muted-foreground">Send payment reminders to your clients</p>
        </div>
        <Button onClick={() => setShowNewReminder(true)} className="bg-accent hover:bg-accent/90">
          <Send className="w-4 h-4 mr-2" />
          New Reminder
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-card rounded-lg border">
          <div className="text-2xl font-bold">{scheduled.length}</div>
          <div className="text-sm text-muted-foreground">Scheduled</div>
        </div>
        <div className="p-4 bg-card rounded-lg border">
          <div className="text-2xl font-bold">{templates.filter(t => t.template_type === 'preset').length}</div>
          <div className="text-sm text-muted-foreground">Templates</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-hidden">
        {/* Quick Send */}
        <div className="bg-card rounded-lg border p-4 overflow-auto">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-accent" />
            Quick Templates
          </h3>
          <p className="text-sm text-muted-foreground mb-4">Click a template to send a reminder</p>
          
          <div className="space-y-3">
            {templates.filter(t => t.template_type === 'preset').map(template => (
              <TemplateCard 
                key={template.id} 
                template={template} 
                leads={leads}
                onSent={fetchData}
              />
            ))}
          </div>
        </div>

        {/* Scheduled Reminders */}
        <div className="bg-card rounded-lg border p-4 overflow-auto">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" />
            Scheduled Reminders
          </h3>
          
          {scheduled.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No scheduled reminders</p>
              <p className="text-sm">Click "New Reminder" to schedule one</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduled.map(reminder => (
                <div key={reminder.id} className="p-3 bg-background rounded-lg border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium">{reminder.recipient_name}</div>
                      <div className="text-sm text-muted-foreground">{reminder.recipient_email}</div>
                      {reminder.amount && (
                        <Badge variant="outline" className="mt-1">
                          ${reminder.amount.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleCancel(reminder.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {format(parseISO(reminder.scheduled_for), 'MMM d, yyyy h:mm a')}
                    {reminder.is_recurring && (
                      <Badge variant="secondary" className="text-xs">
                        <Repeat className="w-3 h-3 mr-1" />
                        {reminder.recurrence_type}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* New Reminder Modal */}
      <NewReminderModal
        open={showNewReminder}
        onClose={() => setShowNewReminder(false)}
        templates={templates}
        leads={leads}
        onCreated={fetchData}
      />
    </div>
  );
};

// Template Card - Click to quickly send
const TemplateCard = ({ template, leads, onSent }) => {
  const [showSendModal, setShowSendModal] = useState(false);
  
  const toneColors = {
    friendly: 'border-green-500/30 bg-green-500/10',
    professional: 'border-blue-500/30 bg-blue-500/10',
    firm: 'border-orange-500/30 bg-orange-500/10',
  };

  return (
    <>
      <button
        onClick={() => setShowSendModal(true)}
        className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:scale-[1.02] ${toneColors[template.tone] || 'border-muted'}`}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">{template.name}</span>
          <Badge variant="outline" className="text-xs capitalize">{template.tone}</Badge>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2">{template.subject}</p>
      </button>

      <QuickSendModal
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        template={template}
        leads={leads}
        onSent={onSent}
      />
    </>
  );
};

// Quick Send Modal - Super simple
const QuickSendModal = ({ open, onClose, template, leads, onSent }) => {
  const [selectedLead, setSelectedLead] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customName, setCustomName] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const selectedLeadData = leads.find(l => l.id === selectedLead);

  const handlePreview = async () => {
    try {
      const res = await previewPaymentReminder({
        template_id: template.id,
        recipient_name: selectedLeadData?.business_name || customName || 'Client Name',
        recipient_email: selectedLeadData?.email || customEmail || 'email@example.com',
        amount: amount ? parseFloat(amount) : null,
        due_date: dueDate || null,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error('Failed to preview');
    }
  };

  const handleSend = async () => {
    const recipientName = selectedLeadData?.business_name || customName;
    const recipientEmail = selectedLeadData?.email || customEmail;

    if (!recipientName || !recipientEmail) {
      toast.error('Please select a client or enter recipient details');
      return;
    }

    setSending(true);
    try {
      await sendPaymentReminderNow({
        template_id: template.id,
        lead_id: selectedLead || null,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        amount: amount ? parseFloat(amount) : null,
        due_date: dueDate || null,
        scheduled_for: new Date().toISOString(),
      });
      toast.success(`Reminder sent to ${recipientEmail}!`);
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-accent" />
            Send "{template.name}"
          </DialogTitle>
          <DialogDescription>Fill in the details below</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pick a Client OR Enter Manually */}
          <div className="space-y-2">
            <Label>Who are you sending this to?</Label>
            <Select value={selectedLead || "manual"} onValueChange={(v) => { setSelectedLead(v === "manual" ? "" : v); setCustomEmail(''); setCustomName(''); }}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a client from your leads..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">-- Enter manually --</SelectItem>
                {leads.map(lead => (
                  <SelectItem key={lead.id} value={lead.id}>
                    {lead.business_name} {lead.email && `(${lead.email})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Manual Entry */}
          {!selectedLead && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input
                  placeholder="John Smith"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  placeholder="john@company.com"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Amount & Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> Amount (optional)
              </Label>
              <Input
                type="number"
                placeholder="2500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Due Date (optional)
              </Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="p-3 bg-muted/30 rounded-lg border">
              <div className="text-xs text-muted-foreground mb-1">Preview:</div>
              <div className="font-medium text-sm">{preview.subject}</div>
              <div className="text-xs text-muted-foreground mt-2" dangerouslySetInnerHTML={{ __html: preview.body }} />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handlePreview} className="flex-1">
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>
            <Button onClick={handleSend} disabled={sending} className="flex-1 bg-accent hover:bg-accent/90">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Send Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// New Reminder Modal - Schedule for later
const NewReminderModal = ({ open, onClose, templates, leads, onCreated }) => {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedLead, setSelectedLead] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customName, setCustomName] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sendDate, setSendDate] = useState('');
  const [sendTime, setSendTime] = useState('09:00');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('monthly');
  const [saving, setSaving] = useState(false);

  const selectedLeadData = leads.find(l => l.id === selectedLead);
  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  const handleSchedule = async () => {
    const recipientName = selectedLeadData?.business_name || customName;
    const recipientEmail = selectedLeadData?.email || customEmail;

    if (!recipientName || !recipientEmail) {
      toast.error('Please select a client or enter recipient details');
      return;
    }
    if (!sendDate) {
      toast.error('Please select when to send');
      return;
    }

    setSaving(true);
    try {
      const scheduledFor = new Date(`${sendDate}T${sendTime}`);
      
      await schedulePaymentReminder({
        template_id: selectedTemplate || null,
        lead_id: selectedLead || null,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        amount: amount ? parseFloat(amount) : null,
        due_date: dueDate || null,
        scheduled_for: scheduledFor.toISOString(),
        is_recurring: isRecurring,
        recurrence_type: isRecurring ? recurrenceType : null,
        recurrence_interval: 1,
      });
      
      toast.success('Reminder scheduled!');
      onCreated();
      onClose();
      // Reset
      setStep(1);
      setSelectedTemplate('');
      setSelectedLead('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-accent" />
            Schedule Payment Reminder
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Step 1: Pick a template'}
            {step === 2 && 'Step 2: Who is this for?'}
            {step === 3 && 'Step 3: When to send?'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Pick Template */}
          {step === 1 && (
            <div className="space-y-3">
              {templates.filter(t => t.template_type === 'preset').map(template => (
                <button
                  key={template.id}
                  onClick={() => { setSelectedTemplate(template.id); setStep(2); }}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:border-accent ${
                    selectedTemplate === template.id ? 'border-accent bg-accent/10' : 'border-muted'
                  }`}
                >
                  <div className="font-medium">{template.name}</div>
                  <div className="text-sm text-muted-foreground">{template.subject}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Who */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Pick a client</Label>
                <Select value={selectedLead || "manual"} onValueChange={(v) => setSelectedLead(v === "manual" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select from your leads..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">-- Enter manually --</SelectItem>
                    {leads.map(lead => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!selectedLead && (
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
                  <Input placeholder="Email" type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Amount</Label>
                  <Input type="number" placeholder="2500" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Due Date</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 bg-accent hover:bg-accent/90">Next</Button>
              </div>
            </div>
          )}

          {/* Step 3: When */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Send on</Label>
                  <Input type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">At time</Label>
                  <Input type="time" value={sendTime} onChange={(e) => setSendTime(e.target.value)} />
                </div>
              </div>

              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-accent" />
                    <Label>Repeat this reminder?</Label>
                  </div>
                  <Switch checked={isRecurring} onCheckedChange={setIsRecurring} />
                </div>
                {isRecurring && (
                  <div className="mt-3">
                    <Select value={recurrenceType} onValueChange={setRecurrenceType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Every Week</SelectItem>
                        <SelectItem value="monthly">Every Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="p-3 bg-accent/10 rounded-lg border border-accent/30">
                <div className="text-sm font-medium mb-2">Summary</div>
                <div className="text-xs space-y-1 text-muted-foreground">
                  <p><strong>To:</strong> {selectedLeadData?.business_name || customName || 'Not set'}</p>
                  <p><strong>Template:</strong> {selectedTemplateData?.name}</p>
                  <p><strong>Amount:</strong> {amount ? `$${parseFloat(amount).toLocaleString()}` : 'Not set'}</p>
                  <p><strong>Send:</strong> {sendDate} at {sendTime}</p>
                  {isRecurring && <p><strong>Repeat:</strong> {recurrenceType}</p>}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={handleSchedule} disabled={saving} className="flex-1 bg-accent hover:bg-accent/90">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  Schedule It
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRemindersView;
